// Shared "estimated" marker (2026-10-24) — the one way every page shows a GPS value that was
// estimated rather than measured. Used for KNVB-based match estimates (estimation_basis.method
// 'knvb_v1', see services/knvbEstimate/) and works for any row with is_estimated.
//
// Display rules (Checkpoint 1): the 80% interval is shown inline for SD and HMLD, in the tooltip for
// TD and HID (estimation_basis.metrics[m].interval.display says which). A metric the estimate did
// not produce (estimation_basis.empty_metrics) renders as "—" with "niet geschat", never as 0.
// Nothing here computes an estimate — it only renders what the API returned.
import React from 'react';

const AMBER = '#f59e0b';
// Hatching behind an estimated value, so it reads as different even without hovering.
const HATCH = 'repeating-linear-gradient(135deg, rgba(245,158,11,0.16) 0 3px, transparent 3px 7px)';

export function parseBasis(basis) {
  if (!basis) return null;
  if (typeof basis === 'string') { try { return JSON.parse(basis); } catch { return null; } }
  return basis;
}

// First estimation_basis found on a player's rows (half rows carry the player's one basis).
export function basisOfRows(rows) {
  for (const r of rows || []) { const b = parseBasis(r.estimation_basis); if (b) return b; }
  return null;
}

export function isEstimatedMetric(basis, metric) {
  const b = parseBasis(basis);
  return !!b && !(b.empty_metrics || []).includes(metric);
}

const pct = q => Math.round(Math.abs(q - 1) * 100);

// "Geschat uit KNVB-tracking, methode knvb_v1, ±x%" — ±x% is the wider side of the 80% interval.
export function estimateTooltip(basis, metric, fmt = v => Math.round(v).toLocaleString()) {
  const b = parseBasis(basis);
  if (!b) return 'Geschat';
  const src = b.method === 'knvb_v1' ? 'Geschat uit KNVB-tracking' : 'Geschat';
  const head = `${src}, methode ${b.method || b.estimation_method || '?'}${b.model_version ? ` (${b.model_version})` : ''}`;
  if (!metric) return head;
  if ((b.empty_metrics || []).includes(metric)) return `${head} — niet geschat voor deze metriek`;
  const m = b.metrics?.[metric];
  const iv = m?.interval;
  let text = head;
  if (iv) text += `, ±${Math.max(pct(iv.q10), pct(iv.q90))}% (80%: ${fmt(iv.lo)}–${fmt(iv.hi)})`;
  if (metric === 'max_speed' && m) {
    text += m.capped ? `, begrensd op persoonlijk max ${fmt(m.personal_max)}` : '';
  }
  return text;
}

// The small "~" itself.
export function EstimatedMarker({ basis, metric, style }) {
  return (
    <span title={estimateTooltip(basis, metric)} aria-label="geschat"
      style={{ color: AMBER, fontWeight: 800, marginLeft: 3, cursor: 'help', ...style }}>~</span>
  );
}

// A value cell's content. Measured (no basis): just the formatted value. Estimated: hatched value +
// "~", inline interval for SD/HMLD, tooltip for everything. Not estimated for this metric: "—".
export function EstimatedValue({ value, basis, metric, format = v => Math.round(v).toLocaleString(), inline }) {
  const b = parseBasis(basis);
  const plain = <>{value == null || Number.isNaN(value) ? '—' : format(value)}</>;
  if (!b) return plain;
  const empty = (b.empty_metrics || []).includes(metric);
  // A training estimate (gpsEstimation.js) only covers some metrics; the rest of that row is
  // measured and renders plain. Metric keys: td/hid/sd/hmld/max_speed/... as in estimation_basis.
  if (!empty && b.metrics && !b.metrics[metric]) return plain;
  if (empty || value == null) {
    return <span title={estimateTooltip(b, metric)} style={{ color: 'var(--text-dimmed)', cursor: 'help' }}>— <span style={{ fontSize: 10 }}>niet geschat</span></span>;
  }
  const iv = b.metrics?.[metric]?.interval;
  const showInline = inline ?? iv?.display === 'inline';
  return (
    <span title={estimateTooltip(b, metric, format)} style={{ background: HATCH, borderRadius: 3, padding: '0 3px', cursor: 'help', whiteSpace: 'nowrap' }}>
      {format(value)}<span style={{ color: AMBER, fontWeight: 800, marginLeft: 2 }}>~</span>
      {showInline && iv && (
        // ± half the 80% interval's width (Frederik, 2026-10-24: "±50" reads faster than a range).
        // The interval isn't exactly symmetric; the tooltip keeps the exact lo–hi.
        <span style={{ fontSize: 10, color: 'var(--text-muted)', marginLeft: 4 }}>±{format((iv.hi - iv.lo) / 2)}</span>
      )}
    </span>
  );
}

// Under a total: "2 estimated", plus which metrics leave them out.
// counts: { nEstimated, notEstimated: { metricLabel: n, ... } }
export function EstimatedTotalsNote({ nEstimated, notEstimated = {}, style }) {
  if (!nEstimated) return null;
  const missing = Object.entries(notEstimated).filter(([, n]) => n > 0);
  return (
    <div style={{ fontSize: 11, color: 'var(--text-muted)', ...style }}>
      <span style={{ background: HATCH, borderRadius: 3, padding: '0 4px' }}>
        <span style={{ color: AMBER, fontWeight: 800 }}>~</span> {nEstimated} estimated
      </span>
      {' '}from KNVB tracking (totals include the estimates)
      {missing.length > 0 && <> · not estimated for {missing.map(([k, n]) => `${k} (${n})`).join(', ')}: those totals exclude {nEstimated === 1 ? 'him' : 'them'}</>}
    </div>
  );
}

// Small superscript on a total cell whose metric some estimated players don't have.
export function NotEstimatedFlag({ n }) {
  if (!n) return null;
  return <sup title={`Niet geschat voor ${n} geschatte speler${n === 1 ? '' : 's'} — totaal zonder ${n === 1 ? 'hem' : 'hen'}`} style={{ color: AMBER, marginLeft: 2, cursor: 'help' }}>excl. {n}</sup>;
}
